// import {forwardRef, useState, useEffect} from "react";
// import {useTiptapEditor} from "components/RichEditor/hooks/use-tiptap-editor";
// import {Input} from "components/RichEditor/components/tiptap-ui-primitive/input/input";
// import {Button} from "components/RichEditor/components/tiptap-ui-primitive/button";

// export const FontSizeControl = forwardRef(({editor: providedEditor}, ref) => {
//   const {editor} = useTiptapEditor(providedEditor);

//   const currentSizeRaw = editor?.getAttributes("textStyle")?.fontSize || "16px";
//   const currentSize = parseInt(currentSizeRaw, 10);

//   const [value, setValue] = useState(currentSize);

//   useEffect(() => {
//     setValue(currentSize);
//   }, [currentSize]);

//   if (!editor) return null;

//   const applyFontSize = (size) => {
//     editor.chain().focus().setFontSize(`${size}px`).run();
//     setValue(size);
//   };

//   const handleInputChange = (e) => {
//     const v = parseInt(e.target.value, 10);
//     setValue(isNaN(v) ? "" : v);
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Enter") {
//       const size = parseInt(value, 10);
//       if (!isNaN(size)) applyFontSize(size);
//     }
//   };

//   const increment = () => applyFontSize(value + 1);
//   const decrement = () => applyFontSize(value - 1);

//   return (
//     <div style={{display: "flex", alignItems: "center"}}>
//       <Button type="button" variant="ghost" onClick={decrement}>
//         -
//       </Button>
//       <Input
//         ref={ref}
//         type="text"
//         value={value}
//         onChange={handleInputChange}
//         onKeyDown={handleKeyDown}
//         style={{width: 50, textAlign: "center"}}
//       />
//       <Button type="button" variant="ghost" onClick={increment}>
//         +
//       </Button>
//     </div>
//   );
// });

// FontSizeControl.displayName = "FontSizeControl";

import {forwardRef, useState, useEffect} from "react";
import {useTiptapEditor} from "components/RichEditor/hooks/use-tiptap-editor";
import {Input} from "components/RichEditor/components/tiptap-ui-primitive/input/input";
import {Button} from "components/RichEditor/components/tiptap-ui-primitive/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
} from "components/RichEditor/components/tiptap-ui-primitive/dropdown-menu";
import {AddIcon} from "components/RichEditor/components/tiptap-icons/add-icon";
import {SubtractIcon} from "components/RichEditor/components/tiptap-icons/subtract-icon";

const FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36];

export const FontSizeControl = forwardRef(({editor: providedEditor}, ref) => {
  const {editor} = useTiptapEditor(providedEditor);

  const currentSizeRaw = editor?.getAttributes("textStyle")?.fontSize || "14px";
  const currentSize = parseInt(currentSizeRaw, 10);

  const [value, setValue] = useState(currentSize);
  const [isOpen, setIsOpen] = useState(false);

  const [isMenuHovered, setIsMenuHovered] = useState(false);

  useEffect(() => setValue(currentSize), [currentSize]);

  if (!editor) return null;

  const applyFontSize = (size) => {
    editor.chain().focus().setFontSize(`${size}px`).run();
    setValue(size);
  };

  const handleInputChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setValue(isNaN(v) ? "" : v);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const size = parseInt(value, 10);
      if (!isNaN(size)) {
        applyFontSize(size);
        setIsOpen(false);
      }
    }
    if (e.key === "Escape") setIsOpen(false);
  };

  const handleSelect = (size) => {
    applyFontSize(size);
    setIsMenuHovered(false);
    setIsOpen(false);
  };

  const increment = () => applyFontSize(value + 1);
  const decrement = () => applyFontSize(value - 1);

  return (
    <div style={{display: "flex", alignItems: "center"}}>
      <Button
        type="button"
        variant="ghost"
        tooltip="RICH_EDITOR_TOOLBAR_DECREASE_BUTTON_TOOLTIP"
        onClick={decrement}
        className="pl-0 pr-0"
      >
        <SubtractIcon />
      </Button>

      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <div style={{position: "relative", margin: "0 2px"}}>
          <DropdownMenuTrigger asChild>
            <div
              style={{position: "absolute", inset: 0, pointerEvents: "none"}}
              aria-hidden="true"
            />
          </DropdownMenuTrigger>

          <Input
            ref={ref}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            onBlur={() => !isMenuHovered && setTimeout(() => setIsOpen(false), 150)}
            style={{width: 34, textAlign: "center", cursor: "text", display: "block"}}
          />
        </div>

        <DropdownMenuContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={() => setIsMenuHovered(true)}
          onMouseLeave={() => setIsMenuHovered(false)}
        >
          <DropdownMenuGroup className="rt-tiptap-editor-dropdown-group">
            {FONT_SIZE_OPTIONS.map((size) => (
              <DropdownMenuItem key={size} asChild>
                <Button
                  type="button"
                  variant="ghost"
                  data-active-state={value === size ? "on" : "off"}
                  aria-label={size}
                  aria-pressed={value === size}
                  showTooltip={false}
                  onClick={() => handleSelect(size)}
                >
                  {size}
                </Button>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        type="button"
        variant="ghost"
        tooltip="RICH_EDITOR_TOOLBAR_INCREASE_BUTTON_TOOLTIP"
        onClick={increment}
        className="pl-0 pr-0"
      >
        <AddIcon />
      </Button>
    </div>
  );
});

FontSizeControl.displayName = "FontSizeControl";
